import * as z from "zod";
import { flag, pagination, uuidV7, version } from "../index";
import { countryCode, organizationRef, shortText } from "./fields";

// The manufacturers the SKU attributes name (FR-M4-07; 50-api/party.md §2.6, ADR-101): who makes the goods, with
// the data an inbound delivery note and the US customs ask for.

/** By name. Without `archived`, the ones not archived. */
export const listManufacturersInput = z.object({
  /** Part of the name or of the code, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  supplierOrgId: z.uuid().optional(),
  /** Only the archived ones instead of the others. */
  archived: flag.optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListManufacturersInput = z.input<typeof listManufacturersInput>;

export const getManufacturerInput = z.object({ manufacturerId: z.uuid() });
export type GetManufacturerInput = z.input<typeof getManufacturerInput>;

/** `null` empties a field. */
export const manufacturerFields = z.object({
  name: shortText(200),
  code: shortText(40).nullable(),
  /** The supplier the manufacturer works for: an organization with the supplier role. */
  supplierOrgId: z.uuid().nullable(),
  addressLine: shortText(200).nullable(),
  city: shortText(100).nullable(),
  stateProvince: shortText(100).nullable(),
  postalCode: shortText(20).nullable(),
  countryCode: countryCode.nullable(),
  /** Manufacturer ID of the US customs. */
  midCode: shortText(40).nullable(),
});

/** The id comes from the client (ADR-046). */
export const createManufacturerInput = manufacturerFields.partial().required({ name: true }).extend({ id: uuidV7 });
export type CreateManufacturerInput = z.input<typeof createManufacturerInput>;

/** PATCH: an absent field stays as it is. */
export const updateManufacturerInput = manufacturerFields.partial().extend({ manufacturerId: z.uuid(), version });
export type UpdateManufacturerInput = z.input<typeof updateManufacturerInput>;

export const manufacturerActionInput = z.object({ manufacturerId: z.uuid(), version });
export type ManufacturerActionInput = z.input<typeof manufacturerActionInput>;

export const manufacturerParams = manufacturerActionInput.pick({ manufacturerId: true });
export const manufacturerVersionBody = manufacturerActionInput.pick({ version: true });
export const updateManufacturerBody = updateManufacturerInput.omit({ manufacturerId: true });

export const manufacturerDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    code: z.string().nullable().optional(),
    supplier: organizationRef.nullable().optional(),
    addressLine: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    stateProvince: z.string().nullable().optional(),
    postalCode: z.string().nullable().optional(),
    countryCode: z.string().nullable().optional(),
    midCode: z.string().nullable().optional(),
    archivedAt: z.iso.datetime().nullable(),
    version: z.number().int(),
  })
  .meta({ id: "Manufacturer" });
